import { Contracts } from "@/components/Payments/RePaymentSubscription";
import { decimalFormatter, longDecimalFormatter } from "@/utils/numberFormatter";
import { useEffect, useMemo, useState } from "react";
import { TElementLine } from "./upgradeSubscriptionRules";
import { elementsWithinBudget } from "../elementsEarthRules";

export type TElementsResponse =
{
    totalWater: number;
    totalIA: number;
    totalEarth: number;
    totalDiscountByElements: number;
    isValid: boolean;
    // Elements usados por contrato (cantidades positivas), para enviarlos en el pago.
    lines: TElementLine[];
};

type TElementType = "water" | "ia" | "earth";

type TElementsTableProps =
{
    savingPlansElements: Contracts[];
    elementWaterPrice: number;
    // Precio de un Element Tierra que da el backend (subscriptionOptions). Con 0 no se pueden usar.
    elementEarthPrice: number;
    newSubscriptionPrice: number;
    onTotalsChange?: (
    {
        totalWater,
        totalIA,
        totalEarth,
        totalDiscountByElements,
        isValid,
        lines,
    }: TElementsResponse) => void;
};

const BASE_CLASS = "elementsTable";

const ElementsTable = ({savingPlansElements, elementWaterPrice, elementEarthPrice, newSubscriptionPrice, onTotalsChange}: TElementsTableProps) =>
{
    const [values, setValues] = useState<Record<string, {water: number; ia: number; earth: number}>>({});
    const elementIAPrice = 0.01;
    const isEarthAvailable = elementEarthPrice > 0;

    const calculateTotalsByType = (data: typeof values) =>
    {
        return Object.values(data).reduce((acc, curr) =>
        {
            acc.totalWater += curr?.water || 0;
            acc.totalIA += curr?.ia || 0;
            acc.totalEarth += curr?.earth || 0;
            return acc;
        }, { totalWater: 0, totalIA: 0, totalEarth: 0 });
    };

    const calculateTotal = (data: typeof values) =>
    {
        return Object.values(data).reduce((acc, curr) =>
        {
            const water = curr?.water || 0;
            const ia = curr?.ia || 0;
            const earth = curr?.earth || 0;

            return(
                acc +
                water * elementWaterPrice +
                ia * elementIAPrice +
                earth * elementEarthPrice
            );
        }, 0);
    };

    const totalCost = Math.round(calculateTotal(values) * 100) / 100;
    const isValid = totalCost <= newSubscriptionPrice;

    // Líneas por elementAmountId, con la misma clave que usan los inputs de la tabla.
    const lines = useMemo((): TElementLine[] =>
    {
        return(savingPlansElements ?? []).reduce((acc: TElementLine[], contract: Contracts, index: number) =>
        {
            if(contract.elementAmountId === undefined || contract.elementAmountId === null)
            {
                return acc;
            }

            const key = `${contract.contract}-${contract.savingPlanId}-${index}`;
            acc.push(
            {
                elementAmountId: contract.elementAmountId,
                water: values[key]?.water || 0,
                ia: values[key]?.ia || 0,
                earth: values[key]?.earth || 0,
            });
            return acc;
        }, []);
    }, [savingPlansElements, values]);

    useEffect(() =>
    {
        if(!onTotalsChange)
        {
            return;
        }

        const { totalWater, totalIA, totalEarth } = calculateTotalsByType(values);
        onTotalsChange(
        {
            totalWater: totalWater,
            totalIA: totalIA,
            totalEarth: totalEarth,
            totalDiscountByElements: totalCost,
            isValid: isValid,
            lines: lines,
        });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [values, totalCost, isValid, lines, onTotalsChange]);

    const handleChange = (key: string, type: TElementType, value: string, contract: Contracts) =>
    {
        // Sin precio los Elements Tierra valdrían 0 € y se descontarían igual: no se aceptan.
        if(type === "earth" && !isEarthAvailable)
        {
            return;
        }

        const max = type === "water" ? contract.elementsWater || 0 :
                    type === "earth" ? contract.elementsEarth || 0 :
                    contract.elementsIA || 0;
        const unitPrice = type === "water" ? elementWaterPrice :
                          type === "earth" ? elementEarthPrice :
                          elementIAPrice;

        // coste de todo lo demás (sin este campo)
        const otherCost = Object.entries(values).reduce((acc, [k, v]) =>
        {
            const water = k === key && type === "water" ? 0 : v?.water || 0;
            const ia = k === key && type === "ia" ? 0 : v?.ia || 0;
            const earth = k === key && type === "earth" ? 0 : v?.earth || 0;
            return (
                acc +
                water * elementWaterPrice +
                ia * elementIAPrice +
                earth * elementEarthPrice
            );
        }, 0);

        const budget = Math.max(0, newSubscriptionPrice - otherCost);
        // Sin ruido de coma flotante: con Math.floor, 0,29 € / 0,01 = 28,999999999999996 dejaba un Element IA fuera.
        const maxByBudget = type === "earth" ? elementsWithinBudget(budget, unitPrice) : 
                            unitPrice > 0 ? elementsWithinBudget(budget, unitPrice) :
                            max;
        const numericValue = value === "" ? 0 : Number(value);
        const safeValue = Math.max(0, Math.min(numericValue, max, maxByBudget));

        setValues((prev) => (
        {
            ...prev,
            [key]: { ...prev[key], [type]: safeValue },
        }));
    };

    return(
        <div className={BASE_CLASS}>
            <div className={`${BASE_CLASS}__card`}>
                <div className={`${BASE_CLASS}__tableTitle`}>Utiliza tus Elements</div>
                <table>
                    <thead>
                        <tr>
                            <th>Plan de Cartera</th>
                            <th>Contrato</th>
                            <th>
                                <div className={`${BASE_CLASS}__cell-wrapper`}>
                                    <div className={`${BASE_CLASS}__cell-title`}>
                                        Elements Tierra
                                    </div>
                                    <div className={`${BASE_CLASS}__cell-subrow`}>
                                        <span>Precio</span>
                                        <span>Cantidad</span>
                                    </div>
                                </div>
                            </th>
                            <th>
                                <div className={`${BASE_CLASS}__cell-wrapper`}>
                                    <div className={`${BASE_CLASS}__cell-title`}>
                                        Elements Agua
                                    </div>
                                    <div className={`${BASE_CLASS}__cell-subrow`}>
                                        <span>Precio</span>
                                        <span>Cantidad</span>
                                    </div>
                                </div>
                            </th>
                            <th>
                                <div className={`${BASE_CLASS}__cell-wrapper`}>
                                    <div className={`${BASE_CLASS}__cell-title`}>Elements IA</div>

                                    <div className={`${BASE_CLASS}__cell-subrow`}>
                                        <span>Precio</span>
                                        <span>Cantidad</span>
                                    </div>
                                </div>
                            </th>
                            <th>Elements Tierra</th>
                            <th>Elements Agua</th>
                            <th>Elements IA</th>
                        </tr>
                    </thead>
                    <tbody>
                        {savingPlansElements?.map((contract: Contracts, index: number) =>
                        {
                            const key = `${contract.contract}-${contract.savingPlanId}-${index}`;
                            return (
                                <tr key={key}>
                                    <td>{contract.savingPlanName}</td>
                                    <td>{contract.contract}</td>
                                    <td>
                                        <div className={`${BASE_CLASS}__double-cell`}>
                                            <span>
                                                {longDecimalFormatter.format(elementEarthPrice)}€
                                            </span>

                                            <span>
                                                {decimalFormatter.format(contract.elementsEarth ?? 0)}
                                            </span>
                                        </div>
                                    </td>
                                    <td>
                                        <div className={`${BASE_CLASS}__double-cell`}>
                                            <span>
                                                {longDecimalFormatter.format(elementWaterPrice)}€
                                            </span>

                                            <span>
                                                {decimalFormatter.format(contract.elementsWater ?? 0)}
                                            </span>
                                        </div>
                                    </td>
                                    <td>
                                        <div className={`${BASE_CLASS}__double-cell`}>
                                            <span>{elementIAPrice}€</span>
                                            <span>
                                                {longDecimalFormatter.format(contract.elementsIA ?? 0)}
                                            </span>
                                        </div>
                                    </td>
                                    <td>
                                        <input
                                            className={`${BASE_CLASS}__input`}
                                            type="number"
                                            value={values[key]?.earth === 0 ? "" : values[key]?.earth}
                                            max={contract.elementsEarth}
                                            disabled={!isEarthAvailable}
                                            onChange={(e) =>
                                                handleChange(key, "earth", e.target.value, contract)
                                            }
                                            placeholder="0"
                                        />
                                    </td>
                                    <td>
                                        <input
                                            className={`${BASE_CLASS}__input`}
                                            type="number"
                                            value={values[key]?.water === 0 ? "" : values[key]?.water}
                                            max={contract.elementsWater}
                                            onChange={(e) =>
                                                handleChange(key, "water", e.target.value, contract)
                                            }
                                            placeholder="0"
                                        />
                                    </td>
                                    <td>
                                        <input
                                            className={`${BASE_CLASS}__input`}
                                            type="number"
                                            value={values[key]?.ia === 0 ? "" : values[key]?.ia}
                                            max={contract.elementsIA}
                                            onChange={(e) =>
                                                handleChange(key, "ia", e.target.value, contract)
                                            }
                                            placeholder="0"
                                        />
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
                {!isValid &&
                (
                    <div className={`${BASE_CLASS}__errorMessage`}>
                        El descuento por Elements no puede superar el precio de la
                        suscripción.
                    </div>
                )}
            </div>
        </div>
    );
};

export default ElementsTable;